import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, CheckCircle2, AlertCircle, Loader2, Plane, Lock, LogOut, Mail, ArrowRight, FileEdit, KeyRound } from "lucide-react";
import { MEMBERS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "theditch_members_token";           // legacy magic-link token
const AUTH_STORAGE_KEY = "theditch_members_session";    // new password session
const MAX_POLLS = 20;

export default function Members() {
  // Phases: checking | dashboard | login | login_sent | error | confirming_payment
  //         | reset_password | password_set
  const [phase, setPhase] = useState("checking");
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginMode, setLoginMode] = useState("password");   // "password" | "magic"
  const [loginLoading, setLoginLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [setPwOpen, setSetPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const polls = useRef(0);

  const fetchAccess = async (token) => {
    try {
      const { data: payload } = await axios.get(`${API}/members/access/${token}`);
      setData(payload);
      localStorage.setItem(STORAGE_KEY, token);
      setPhase("dashboard");
      // Fetch account status to know if we should show the "Set a password" upsell
      fetchAccount();
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
      const status = err.response?.status;
      if (status === 410) {
        setErrorMsg("Your members link expired (60-day limit). Pop your email in below to get a fresh one.");
        setPhase("login");
      } else if (status === 403) {
        setErrorMsg("Your subscription isn't active right now. If you cancelled or this looks wrong, drop us a line.");
        setPhase("error");
      } else {
        setErrorMsg("That members link isn't valid — request a new one.");
        setPhase("login");
      }
    }
  };

  const fetchAccount = async () => {
    const sess = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!sess) { setAccount(null); return; }
    try {
      const { data: me } = await axios.get(`${API}/members/auth/me`, {
        headers: { Authorization: `Bearer ${sess}` },
        withCredentials: true,
      });
      setAccount(me);
    } catch {
      // session probably expired — silently drop it
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setAccount(null);
    }
  };

  const signInWithPassword = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(loginEmail)) { toast.error("Pop in a valid email."); return; }
    if (loginPassword.length < 1) { toast.error("Enter your password."); return; }
    setLoginLoading(true);
    try {
      const { data: res } = await axios.post(
        `${API}/members/auth/login`,
        { email: loginEmail.trim(), password: loginPassword },
        { withCredentials: true },
      );
      localStorage.setItem(AUTH_STORAGE_KEY, res.session_token);
      setAccount({ user: res.user, subscription_active: true, has_password: true });
      toast.success(`Welcome back, ${res.user.first_name || res.user.email}!`);
      // Bootstrap dashboard using a fresh magic-link
      await axios.post(`${API}/members/login`, { email: loginEmail.trim() });
      // Also try to hit /members/access via any existing token to skip email round-trip
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing) { fetchAccess(existing); }
      else { setPhase("login_sent"); }
    } catch (err) {
      const s = err.response?.status;
      if (s === 429) toast.error(err.response?.data?.detail || "Too many attempts — try again in 15 min.");
      else if (s === 401) toast.error("Incorrect email or password.");
      else toast.error("Sign-in failed — please try again.");
    } finally { setLoginLoading(false); }
  };

  const forgotPassword = async () => {
    if (!/^\S+@\S+\.\S+$/.test(loginEmail)) {
      toast.error("Enter your email above first."); return;
    }
    try {
      await axios.post(`${API}/members/auth/forgot-password`, { email: loginEmail.trim() });
      toast.success("If that email exists, we sent a reset link. Check your inbox.");
    } catch { toast.error("Couldn't send that — try again."); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPassword.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoginLoading(true);
    try {
      const { data: res } = await axios.post(
        `${API}/members/auth/reset-password`,
        { token: resetToken, password: resetPassword },
        { withCredentials: true },
      );
      localStorage.setItem(AUTH_STORAGE_KEY, res.session_token);
      setAccount({ user: res.user, subscription_active: true, has_password: true });
      toast.success("Password updated — you're signed in.");
      // Reset the state and try to bootstrap dashboard via existing magic-link
      setResetToken(""); setResetPassword("");
      window.history.replaceState({}, document.title, "/members");
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing) { fetchAccess(existing); }
      else {
        await axios.post(`${API}/members/login`, { email: res.user.email });
        setPhase("login_sent");
      }
    } catch (err) {
      const s = err.response?.status;
      if (s === 410) toast.error("Reset link expired — request a fresh one.");
      else if (s === 400) toast.error(err.response?.data?.detail || "Reset link is invalid.");
      else toast.error("Password reset failed — try again.");
    } finally { setLoginLoading(false); }
  };

  const setDashboardPassword = async () => {
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    const sess = localStorage.getItem(AUTH_STORAGE_KEY);
    setPwSaving(true);
    try {
      // If no session yet (magic-link only), auto-register the account via the
      // /register endpoint (subscription is confirmed by /members/access already).
      if (!sess) {
        const { data: reg } = await axios.post(
          `${API}/members/auth/register`,
          { email: data.email, password: newPw, first_name: data.first_name || "" },
          { withCredentials: true },
        );
        localStorage.setItem(AUTH_STORAGE_KEY, reg.session_token);
      } else {
        await axios.post(
          `${API}/members/auth/set-password`,
          { password: newPw },
          { headers: { Authorization: `Bearer ${sess}` }, withCredentials: true },
        );
      }
      toast.success("Password set — you can now sign in from any device.");
      setSetPwOpen(false); setNewPw("");
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't set password — try again.");
    } finally { setPwSaving(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const urlToken = params.get("token");
    const resetParam = params.get("reset");

    if (resetParam) {
      setResetToken(resetParam);
      setPhase("reset_password");
      return;
    }

    if (sessionId) {
      setPhase("confirming_payment");
      const pollPayment = async () => {
        if (polls.current >= MAX_POLLS) {
          setErrorMsg("We couldn't confirm your subscription in time — if you were charged, check your email for your members link.");
          setPhase("error");
          return;
        }
        polls.current += 1;
        try {
          const { data: status } = await axios.get(`${API}/subscription/status/${sessionId}`);
          if (status.payment_status === "paid" && status.members_token) {
            // Clean URL so refresh doesn't re-poll.
            window.history.replaceState({}, document.title, "/members");
            fetchAccess(status.members_token);
          } else {
            setTimeout(pollPayment, 2000);
          }
        } catch {
          setErrorMsg("Something went wrong confirming your subscription.");
          setPhase("error");
        }
      };
      pollPayment();
      return;
    }

    if (urlToken) {
      window.history.replaceState({}, document.title, "/members");
      fetchAccess(urlToken);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      fetchAccess(stored);
      return;
    }

    // No magic-link token — check if we have a password session
    const passSess = localStorage.getItem(AUTH_STORAGE_KEY);
    if (passSess) {
      // Password session lets us know they're logged in, but /members/access
      // still needs a members_token — issue a fresh one via /members/login.
      axios.get(`${API}/members/auth/me`, {
        headers: { Authorization: `Bearer ${passSess}` },
        withCredentials: true,
      }).then(({ data: me }) => {
        setAccount(me);
        // Fire magic-link so /members/access can hydrate the dashboard next visit.
        axios.post(`${API}/members/login`, { email: me.user.email });
        setErrorMsg("You're signed in — we just emailed you a fresh library link. Click it to load your dashboard here.");
        setPhase("login_sent");
      }).catch(() => {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setPhase("login");
      });
      return;
    }

    setPhase("login");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  const sendMagicLink = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(loginEmail)) {
      toast.error("Pop in a valid email.");
      return;
    }
    setLoginLoading(true);
    try {
      await axios.post(`${API}/members/login`, { email: loginEmail.trim() });
      setPhase("login_sent");
    } catch {
      toast.error("Couldn't send that — try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const openPortal = async () => {
    if (!data?.email) return;
    setPortalLoading(true);
    try {
      const { data: payload } = await axios.post(`${API}/subscription/portal`, { email: data.email });
      window.location.href = payload.url;
    } catch {
      toast.error("Couldn't open the billing portal. Email hello@theditchcollective.co.nz and we'll sort it.");
      setPortalLoading(false);
    }
  };

  const signOut = async () => {
    const sess = localStorage.getItem(AUTH_STORAGE_KEY);
    if (sess) {
      try {
        await axios.post(`${API}/members/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${sess}` },
          withCredentials: true,
        });
      } catch (err) { console.warn("Members: logout call failed (best-effort)", err); }
    }
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setData(null);
    setAccount(null);
    setPhase("login");
  };

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col">
      <header className="px-5 md:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto w-full">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
        {phase === "dashboard" && (
          <button onClick={signOut} className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] flex items-center gap-1.5">
            <LogOut size={14} /> Sign out
          </button>
        )}
      </header>

      <main className="flex-1 px-4 pb-20">
        {phase === "checking" && (
          <CenterCard>
            <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
            <h1 className="font-display text-2xl font-extrabold mt-6">Checking your membership…</h1>
          </CenterCard>
        )}

        {phase === "confirming_payment" && (
          <CenterCard>
            <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
            <h1 className="font-display text-2xl font-extrabold mt-6">Confirming your subscription…</h1>
            <p className="text-[var(--ocean)] mt-2 text-sm">Cheers for joining — just locking things in.</p>
          </CenterCard>
        )}

        {phase === "login" && (
          <CenterCard>
            <Lock size={36} className="mx-auto text-[var(--ink)]" />
            <span className="num-tag text-xs block mt-6">Members</span>
            <h1 className="font-display text-3xl font-extrabold mt-2 leading-tight">Sign in to your library</h1>
            {errorMsg && (
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
            )}

            {/* Mode toggle */}
            <div className="mt-6 flex items-center justify-center gap-1 p-1 bg-[var(--mist-soft)] rounded-full text-xs font-bold uppercase tracking-widest">
              <button
                type="button"
                data-testid="members-mode-password"
                onClick={() => setLoginMode("password")}
                className={`px-4 py-2 rounded-full transition-colors ${loginMode === "password" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ocean)]"}`}
              >
                Password
              </button>
              <button
                type="button"
                data-testid="members-mode-magic"
                onClick={() => setLoginMode("magic")}
                className={`px-4 py-2 rounded-full transition-colors ${loginMode === "magic" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ocean)]"}`}
              >
                Magic link
              </button>
            </div>

            {loginMode === "password" ? (
              <form onSubmit={signInWithPassword} className="mt-5 space-y-3 text-left">
                <input
                  data-testid={MEMBERS.loginEmail}
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoComplete="email"
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
                />
                <input
                  data-testid="members-login-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
                />
                <button
                  type="submit"
                  data-testid="members-login-password-submit"
                  disabled={loginLoading}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
                >
                  {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <>Sign in <ArrowRight size={16} /></>}
                </button>
                <button
                  type="button"
                  data-testid="members-forgot-password"
                  onClick={forgotPassword}
                  className="text-xs text-[var(--ocean)] hover:text-[var(--ink)] underline underline-offset-2"
                >
                  Forgot your password?
                </button>
              </form>
            ) : (
              <form onSubmit={sendMagicLink} className="mt-5 space-y-3 text-left">
                <input
                  data-testid={MEMBERS.loginEmail}
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoComplete="email"
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
                />
                <button
                  type="submit"
                  data-testid={MEMBERS.loginSubmit}
                  disabled={loginLoading}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
                >
                  {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <>Email me a sign-in link <ArrowRight size={16} /></>}
                </button>
              </form>
            )}

            <p className="mt-5 text-xs text-[var(--ocean)]">
              Not a member yet? <a href="/#guides" className="underline font-bold">Join from the home page</a> for $4.99/mo.
            </p>
          </CenterCard>
        )}

        {phase === "reset_password" && (
          <CenterCard>
            <KeyRound size={36} className="mx-auto text-[var(--ink)]" />
            <h1 className="font-display text-3xl font-extrabold mt-6 leading-tight">Set a new password</h1>
            <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
              Choose something you'll remember — at least 8 characters.
            </p>
            <form onSubmit={submitReset} className="mt-6 space-y-3 text-left">
              <input
                data-testid="members-reset-password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="New password"
                autoComplete="new-password"
                className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
              />
              <button
                type="submit"
                data-testid="members-reset-submit"
                disabled={loginLoading}
                className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
              >
                {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <>Save password <ArrowRight size={16} /></>}
              </button>
            </form>
          </CenterCard>
        )}

        {phase === "login_sent" && (
          <CenterCard>
            <Mail size={40} className="mx-auto text-[var(--lime-deep)]" data-testid={MEMBERS.loginSent} />
            <h1 className="font-display text-2xl font-extrabold mt-6">Check your inbox</h1>
            <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
              If you've got an active subscription we just emailed you a fresh members link.
              Can't see it? Have a peek in spam.
            </p>
            <a href="/" className="mt-6 inline-block btn-ghost text-sm">← Back to the site</a>
          </CenterCard>
        )}

        {phase === "error" && (
          <CenterCard>
            <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
            <h1 className="font-display text-2xl font-extrabold mt-6">Hmm, that didn't work</h1>
            <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
            <p className="text-sm text-[var(--ink)] mt-5">
              Email <a href="mailto:hello@theditchcollective.co.nz" className="font-bold underline">hello@theditchcollective.co.nz</a> and we'll sort you out.
            </p>
            <a href="/" className="mt-6 inline-block btn-ghost text-sm">← Back to the site</a>
          </CenterCard>
        )}

        {phase === "dashboard" && data && (
          <section data-testid={MEMBERS.dashboard} className="max-w-4xl mx-auto pt-6">
            <div className="bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-6 md:p-10 shadow-sm">
              <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                <div>
                  <span className="num-tag text-xs">{data.active_package_name || "Members library"}</span>
                  <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-2 leading-tight">
                    Welcome back{data.first_name ? `, ${data.first_name}` : ""}
                  </h1>
                  <p className="text-[var(--ocean)] mt-1 text-sm">
                    {data.subscription?.comped ? (
                      <>Comped Members access · enjoy the library</>
                    ) : (
                      <>Active subscription · renewing {data.subscription?.current_period_end ? new Date(data.subscription.current_period_end).toLocaleDateString() : "monthly"}</>
                    )}
                  </p>
                </div>
                {data.subscription?.has_stripe_customer && (
                  <button
                    data-testid={MEMBERS.managePortal}
                    onClick={openPortal}
                    disabled={portalLoading}
                    className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] underline disabled:opacity-60"
                  >
                    {portalLoading ? "Opening…" : "Manage subscription"}
                  </button>
                )}
              </div>
              {data.active_package && data.active_package !== "guides_member" && (
                <p className="text-xs text-[var(--ocean)] italic mb-6">
                  Your full {data.active_package_name} library is unlocked below — plus every Members guide.                </p>
              )}

              {/* Password upsell — shown until member has set a password */}
              {(!account || !account.has_password) && (
                <div
                  data-testid="members-set-password-cta"
                  className="mb-6 p-4 rounded-2xl border border-[var(--ink)]/10 bg-[var(--mist-soft)]"
                >
                  {!setPwOpen ? (
                    <div className="flex items-start gap-3 flex-wrap">
                      <KeyRound size={18} className="text-[var(--ink)] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-[220px]">
                        <p className="font-display font-bold text-[var(--ink)] text-sm">Log in from any device</p>
                        <p className="text-xs text-[var(--ocean)] mt-0.5">
                          Set a password so you don't have to wait for a magic-link email each time.
                        </p>
                      </div>
                      <button
                        onClick={() => setSetPwOpen(true)}
                        data-testid="members-set-password-open"
                        className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--ink-soft)]"
                      >
                        Set a password
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="password"
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        placeholder="Choose a password (min 8 chars)"
                        autoComplete="new-password"
                        data-testid="members-new-password"
                        className="flex-1 rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--ink)]"
                      />
                      <button
                        onClick={setDashboardPassword}
                        disabled={pwSaving || newPw.length < 8}
                        data-testid="members-new-password-save"
                        className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-bold px-4 py-2.5 rounded-xl bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--ink-soft)] disabled:opacity-50"
                      >
                        {pwSaving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                      </button>
                      <button
                        onClick={() => { setSetPwOpen(false); setNewPw(""); }}
                        className="text-xs text-[var(--ocean)] hover:text-[var(--ink)]"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {/* Interactive guides — always visible to active members */}
                <a
                  href="/members/pet-resume"
                  data-testid="members-pet-resume-card"
                  className="group rounded-2xl border border-[var(--ink)] bg-[var(--ink)] p-5 text-[var(--paper)] hover:bg-[var(--ink-soft)] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] uppercase tracking-widest font-bold text-[var(--ink)] bg-[var(--lime)] px-2 py-0.5 rounded-full">
                      Interactive · Members
                    </span>
                    <FileEdit size={14} className="text-[var(--lime)]" />
                  </div>
                  <p className="font-display font-bold text-lg leading-snug min-h-[3rem]">
                    Build your Pet Resume — a branded rental application sidekick
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 bg-[var(--lime)] text-[var(--ink)] font-display font-bold rounded-xl px-4 py-2 text-sm">
                    <FileEdit size={14} /> Open builder
                  </span>
                </a>

                {data.guides.map((g) => (
                  <div
                    key={g.guide_id}
                    data-testid={MEMBERS.guideRow(g.guide_id)}
                    className={`rounded-2xl border p-5 ${
                      g.coming_soon
                        ? "border-dashed border-[var(--line)] opacity-70"
                        : !g.unlocked
                          ? "border-[var(--line)] bg-[var(--mist-soft)] opacity-80"
                          : "border-[var(--line)] bg-[var(--cream)]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[9px] uppercase tracking-widest font-bold text-[var(--ocean)] bg-[var(--mist-soft)] px-2 py-0.5 rounded-full">
                        {g.free ? "Free" : g.coming_soon ? "Coming soon" : !g.unlocked ? "Upgrade to unlock" : "Members"}
                      </span>
                      {!g.coming_soon && (g.unlocked ? <Download size={14} className="text-[var(--ink)]" /> : <Lock size={14} className="text-[var(--ocean)]" />)}
                    </div>
                    <p className="font-display font-bold text-lg leading-snug min-h-[3rem]">{g.title}</p>
                    {g.coming_soon ? (
                      <p className="mt-3 text-xs text-[var(--ocean)]">Dropping in your library soon.</p>
                    ) : !g.unlocked ? (
                      <a
                        href="/#packages"
                        className="mt-4 inline-flex items-center gap-2 border border-[var(--ink)] text-[var(--ink)] font-display font-bold rounded-xl px-4 py-2 text-sm hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors"
                      >
                        <Lock size={14} /> Upgrade to read
                      </a>
                    ) : (
                      <a
                        data-testid={MEMBERS.downloadBtn(g.guide_id)}
                        href={`${API}/guides/download/${g.download_token}/file`}
                        className="mt-4 inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-4 py-2 text-sm hover:bg-[var(--ink-soft)] transition-colors"
                      >
                        <Download size={14} /> Download PDF
                      </a>
                    )}
                  </div>
                ))}
              </div>

              <p className="mt-8 text-xs text-[var(--ocean)] text-center">
                Download links refresh every visit and stay valid for 7 days. Questions? hello@theditchcollective.co.nz
              </p>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-lg bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl">
        {children}
      </div>
    </div>
  );
}
